/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';
import { Button, Input, message, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { queryCache, useMutation, useQuery } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { getShareMessage, postShareMessage } from '~src/services/api/shares';
import { appLog } from '~src/utils/log';
import { TenderRegistrationVM } from '~src/types/db';

const { TextArea } = Input;

interface TenderInvitationTabProps {
  projectId?: number;
  tenderId?: number | string;
  tenderRegistration?: TenderRegistrationVM;
  isVisible?: boolean;
}

export const TenderInvitationTab: React.FC<TenderInvitationTabProps> = ({
  projectId,
  tenderId,
  tenderRegistration,
  isVisible,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const t = (id: string, def?: string) =>
    intl.formatMessage({ id, defaultMessage: def });

  const tenderIsCompleted = tenderRegistration?.state === 6;

  const [isInvitationEditing, setIsInvitationEditing] = useState(false);
  const [isInvitationDirty, setIsInvitationDirty] = useState(false);
  const [isInvitationSaving, setIsInvitationSaving] = useState(false);

  const { data: shareMainMessage, isLoading: isShareMessageLoading } = useQuery(
    ['share-invite-message', projectId, tenderId],
    () => getShareMessage({ projectId, tenderId }),
    {
      enabled: !!projectId && !!tenderId && !!isVisible,
    }
  );

  const sharesForm = useForm<{ title: string; message: string }>({
    defaultValues: {
      title: shareMainMessage?.title || '',
      message: shareMainMessage?.description || '',
    },
  });

  const invitationTitle = sharesForm.watch('title');
  const invitationMessage = sharesForm.watch('message');

  const [
    doPostInvitationMessage,
    { isLoading: isPostInvitationMessageLoading },
  ] = useMutation(postShareMessage, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        'share-invite-message',
        projectId,
        tenderId,
      ]);
      message.success(
        t('invite-share-success', 'Invitations posted successfully.')
      );
    },
    onError: (error, item) => {
      message.error(
        t('invite-share-edit-failed', 'Failed to post invitation message')
      );
      appLog('share error', error, item);
    },
  });

  const isInvitationLoading =
    isShareMessageLoading || isPostInvitationMessageLoading;

  const isInvitationReadOnly =
    !isInvitationEditing ||
    isInvitationLoading ||
    isInvitationSaving ||
    tenderIsCompleted;

  useEffect(() => {
    if (!projectId || !tenderId) return;

    appLog('share-invitation-load', { projectId, tenderId });
    setIsInvitationDirty(false);
  }, [projectId, tenderId]);

  useEffect(() => {
    if (!isVisible) return;
    if (!shareMainMessage) return;

    const tpl = Array.isArray(shareMainMessage)
      ? shareMainMessage[0]
      : shareMainMessage;

    if (!tpl) return;

    if (isInvitationEditing && isInvitationDirty) {
      return;
    }

    sharesForm.reset({
      title: tpl.title || '',
      message: tpl.description || '',
    });

    setIsInvitationDirty(false);
  }, [
    isVisible,
    shareMainMessage,
    isInvitationEditing,
    isInvitationDirty,
    sharesForm,
  ]);

  const handleSaveInvitation = async () => {
    const values = sharesForm.getValues();

    if (!projectId || !tenderId) {
      message.error(
        t('invite-share-edit-failed', 'Failed to post invitation message')
      );
      return;
    }

    appLog('share-invitation-save', {
      projectId,
      tenderId,
      title: values.title,
      message: values.message,
      mode: 'tender-options',
    });

    setIsInvitationSaving(true);

    try {
      await doPostInvitationMessage({
        projectId,
        tenderId,
        body: { title: values.title, description: values.message },
      } as any);

      setIsInvitationEditing(false);
      setIsInvitationDirty(false);
    } finally {
      setIsInvitationSaving(false);
    }
  };

  const handleInvitationEditClick = () => {
    if (!isInvitationEditing) {
      setIsInvitationEditing(true);
      setIsInvitationDirty(false);
      return;
    }

    if (!isInvitationSaving) {
      handleSaveInvitation();
    }
  };

  return (
    <div css={styles.section}>
      {tenderIsCompleted && (
        <div css={styles.closedWarning}>
          <FontAwesomeIcon
            icon={['fas', 'exclamation-circle']}
            style={{ marginRight: '8px' }}
          />
          {t(
            'invite-disabled-tender-closed',
            'Invitation editing is disabled because tender is closed.'
          )}
        </div>
      )}

      <div css={styles.sectionHeader}>
        <div css={styles.sectionTitle}>
          <FontAwesomeIcon icon={['far', 'file-lines']} />
          <span>{t('invite-invitation', 'Invitation')}</span>
        </div>

        <div css={styles.headerActions}>
          <Tooltip
            title={
              isInvitationEditing
                ? t(
                    'invite-save-invitation-tooltip',
                    'Save the current invitation text'
                  )
                : t(
                    'invite-edit-invitation-tooltip',
                    'Edit the invitation title and message'
                  )
            }>
            <Button
              size="small"
              onClick={handleInvitationEditClick}
              loading={isInvitationSaving}
              disabled={isInvitationLoading || tenderIsCompleted}>
              <FontAwesomeIcon
                icon={
                  isInvitationEditing ? ['fas', 'lock-open'] : ['far', 'lock']
                }
                style={{ marginRight: 6 }}
              />
              {isInvitationEditing
                ? t('invite-save-invitation', 'Save')
                : t('invite-edit-invitation', 'Edit')}
            </Button>
          </Tooltip>
        </div>
      </div>

      <div css={styles.letterGrid}>
        <div>
          <div css={styles.fieldLabel}>{t('invite-title', 'Title')}</div>
          <Input
            value={invitationTitle}
            maxLength={200}
            placeholder={t('invite-enter-title', 'Enter title')}
            disabled={isInvitationReadOnly}
            onChange={(e) => {
              sharesForm.setValue('title', e.target.value, {
                shouldDirty: true,
              });
              if (isInvitationEditing) setIsInvitationDirty(true);
            }}
          />
        </div>

        <div>
          <div css={styles.fieldLabel}>{t('invite-message', 'Message')}</div>
          <TextArea
            value={invitationMessage}
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder={t('invite-enter-message', 'Message')}
            disabled={isInvitationReadOnly}
            onChange={(e) => {
              sharesForm.setValue('message', e.target.value, {
                shouldDirty: true,
              });
              if (isInvitationEditing) setIsInvitationDirty(true);
            }}
          />
        </div>

        <div css={styles.noteRow}>
          <FontAwesomeIcon icon={['far', 'info-circle']} />
          <span>
            {t(
              'invite-send-requirements',
              'Need at least 1 valid email, a Title and a Message to send.'
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },

  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 16,
  },

  headerActions: {
    display: 'flex',
    gap: 8,
  },

  letterGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
  },

  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#595959',
    marginBottom: 4,
  },

  noteRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: '#8c8c8c',
  },

  closedWarning: {
    padding: '12px',
    marginBottom: '4px',
    backgroundColor: '#fff1f0',
    border: '1px solid #ffa39e',
    borderRadius: '4px',
    color: '#cf1322',
    fontSize: '14px',
  },
});

export default TenderInvitationTab;
